import { useState, useEffect } from "react";
import {
  Mail,
  Share2,
  FileText,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Hash,
  Plus,
  Trash2,
} from "lucide-react";
import {
  subscribeToContactInfo,
  subscribeToSocialMedia,
  subscribeToTerms,
  subscribeToSectionSettings,
  upsertContactInfo,
  upsertSocialMedia,
  upsertTerms,
  upsertSectionSettings,
} from "../../services/firestore";
import AdminTabs from "./AdminTabs";
import { toastSuccess, toastError, toastPromise } from "./ToastProvider";

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState("contact");
  const [saving, setSaving] = useState(false);

  // Contact Info State
  const [contactInfo, setContactInfo] = useState({
    phone: "",
    whatsapp: "",
    email: "",
    address: "",
    city: "",
    region: "",
    postalCode: "",
    mapUrl: "",
  });

  // Social Media State
  const [socialMedia, setSocialMedia] = useState({
    instagram: "",
    facebook: "",
    tiktok: "",
    twitter: "",
    youtube: "",
  });

  // Terms State
  const [terms, setTerms] = useState([
    {
      id: 1,
      title: "",
      description: "",
      details: [],
    },
  ]);

  // Section Settings State
  const [sectionSettings, setSectionSettings] = useState({
    hero: true,
    about: true,
    services: true,
    gallery: true,
    certificates: true,
    testimonials: true,
    contact: true,
  });

  useEffect(() => {
    // Load all settings
    const unsubscribeContact = subscribeToContactInfo((data) => {
      if (data) setContactInfo(data);
    });

    const unsubscribeSocial = subscribeToSocialMedia((data) => {
      if (data) setSocialMedia(data);
    });

    const unsubscribeTerms = subscribeToTerms((data) => {
      if (data && Array.isArray(data)) {
        setTerms(data);
      } else if (!data || data.length === 0) {
        // Set default structure if empty
        setTerms([
          {
            id: 1,
            title: "Booking Tanggal",
            description: "Booking tanggal wajib melakukan DP:",
            details: [
              "Henna Wedding/Prewedding/Engagement: sebesar 100K",
              "Henna Graduation/Fun Henna/Bridal Family/Pagar Ayu/Bridesmaid: sebesar 40%",
            ],
          },
          {
            id: 2,
            title: "Pelunasan",
            description:
              "Henna Wedding / Pagar Ayu / Bridesmaid / Bridal Family, maksimal dilakukan pada H-7. Prewedding / Engagement / Graduation / Henna Fun, maksimal dilakukan pada H-1.",
            details: [
              "Henna Wedding / Pagar Ayu / Bridesmaid / Bridal Family: maksimal H-7",
              "Prewedding / Engagement / Graduation / Henna Fun: maksimal H-1",
            ],
          },
          {
            id: 3,
            title: "Pembatalan",
            description:
              "Apabila terjadi pembatalan sepihak oleh klien, maka uang DP dinyatakan hangus.",
            details: [],
          },
        ]);
      }
    });

    const unsubscribeSections = subscribeToSectionSettings((data) => {
      if (data) setSectionSettings(data);
    });

    return () => {
      unsubscribeContact();
      unsubscribeSocial();
      unsubscribeTerms();
      unsubscribeSections();
    };
  }, []);

  const tabs = [
    { id: "contact", name: "Kontak", icon: Mail },
    { id: "social", name: "Sosial Media", icon: Share2 },
    { id: "terms", name: "Syarat & Ketentuan", icon: FileText },
    { id: "sections", name: "Bagian Halaman", icon: Eye },
  ];

  const handleSave = async (tab) => {
    setSaving(true);
    try {
      let savePromise;
      let tabName = "";

      if (tab === "contact") {
        savePromise = upsertContactInfo(contactInfo);
        tabName = "kontak";
      } else if (tab === "social") {
        savePromise = upsertSocialMedia(socialMedia);
        tabName = "sosial media";
      } else if (tab === "terms") {
        savePromise = upsertTerms(terms);
        tabName = "syarat & ketentuan";
      } else if (tab === "sections") {
        savePromise = upsertSectionSettings(sectionSettings);
        tabName = "pengaturan halaman";
      }

      await toastPromise(
        savePromise,
        {
          loading: `Menyimpan pengaturan ${tabName}...`,
          success: `Pengaturan ${tabName} berhasil disimpan!`,
          error: `Gagal menyimpan pengaturan ${tabName}`,
        }
      );
    } catch (error) {
      console.error("Error saving settings:", error);
      toastError("Gagal menyimpan pengaturan. Silakan coba lagi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[#F7F5F2]">
          Pengaturan Website
        </h2>
        <p className="text-[#F7F5F2]/70 text-sm mt-1">
          Kelola informasi kontak, sosial media, syarat ketentuan, dan tampilan
          halaman
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#D4AF7A]/20">
        <AdminTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Contact Info Tab */}
        {activeTab === "contact" && (
          <div className="space-y-6">
            <div className="p-6 bg-white/5 border border-[#D4AF7A]/20 rounded-lg">
              <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                Informasi Kontak
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    No. Telepon
                  </label>
                  <input
                    type="text"
                    value={contactInfo.phone}
                    onChange={(e) =>
                      setContactInfo({ ...contactInfo, phone: e.target.value })
                    }
                    placeholder="022-12345678"
                    className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    No. WhatsApp *
                  </label>
                  <input
                    type="text"
                    value={contactInfo.whatsapp}
                    onChange={(e) =>
                      setContactInfo({
                        ...contactInfo,
                        whatsapp: e.target.value,
                      })
                    }
                    placeholder="6289526775445"
                    className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) =>
                      setContactInfo({ ...contactInfo, email: e.target.value })
                    }
                    placeholder="info@tehputrihenna.com"
                    className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    Alamat Lengkap
                  </label>
                  <textarea
                    value={contactInfo.address}
                    onChange={(e) =>
                      setContactInfo({
                        ...contactInfo,
                        address: e.target.value,
                      })
                    }
                    placeholder="Jalan Raya Sumedang No. 123"
                    rows={2}
                    className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Kota
                    </label>
                    <input
                      type="text"
                      value={contactInfo.city}
                      onChange={(e) =>
                        setContactInfo({ ...contactInfo, city: e.target.value })
                      }
                      placeholder="Sumedang"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Provinsi
                    </label>
                    <input
                      type="text"
                      value={contactInfo.region}
                      onChange={(e) =>
                        setContactInfo({
                          ...contactInfo,
                          region: e.target.value,
                        })
                      }
                      placeholder="Jawa Barat"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Kode Pos
                    </label>
                    <input
                      type="text"
                      value={contactInfo.postalCode}
                      onChange={(e) =>
                        setContactInfo({
                          ...contactInfo,
                          postalCode: e.target.value,
                        })
                      }
                      placeholder="45362"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    URL Google Maps (Embed)
                  </label>
                  <input
                    type="text"
                    value={contactInfo.mapUrl}
                    onChange={(e) =>
                      setContactInfo({ ...contactInfo, mapUrl: e.target.value })
                    }
                    placeholder="https://www.google.com/maps/embed?..."
                    className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  />
                  <p className="text-xs text-[#F7F5F2]/60 mt-1">
                    Paste URL embed dari Google Maps
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleSave("contact")}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              Simpan Pengaturan Kontak
            </button>
          </div>
        )}

        {/* Social Media Tab */}
        {activeTab === "social" && (
          <div className="space-y-6">
            <div className="p-6 bg-white/5 border border-[#D4AF7A]/20 rounded-lg">
              <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">
                Media Sosial
              </h3>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                    <Instagram className="w-5 h-5 text-pink-400" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1">
                      Instagram
                    </label>
                    <input
                      type="text"
                      value={socialMedia.instagram}
                      onChange={(e) =>
                        setSocialMedia({
                          ...socialMedia,
                          instagram: e.target.value,
                        })
                      }
                      placeholder="@username atau link profil"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-600/20">
                    <Facebook className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1">
                      Facebook
                    </label>
                    <input
                      type="text"
                      value={socialMedia.facebook}
                      onChange={(e) =>
                        setSocialMedia({
                          ...socialMedia,
                          facebook: e.target.value,
                        })
                      }
                      placeholder="Username atau link halaman"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-black/20">
                    <Hash className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1">
                      TikTok
                    </label>
                    <input
                      type="text"
                      value={socialMedia.tiktok}
                      onChange={(e) =>
                        setSocialMedia({
                          ...socialMedia,
                          tiktok: e.target.value,
                        })
                      }
                      placeholder="@username atau link profil"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-sky-500/20">
                    <Twitter className="w-5 h-5 text-sky-400" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1">
                      Twitter / X
                    </label>
                    <input
                      type="text"
                      value={socialMedia.twitter}
                      onChange={(e) =>
                        setSocialMedia({
                          ...socialMedia,
                          twitter: e.target.value,
                        })
                      }
                      placeholder="@username"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-600/20">
                    <Youtube className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1">
                      YouTube
                    </label>
                    <input
                      type="text"
                      value={socialMedia.youtube}
                      onChange={(e) =>
                        setSocialMedia({
                          ...socialMedia,
                          youtube: e.target.value,
                        })
                      }
                      placeholder="Channel URL atau @username"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleSave("social")}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              Simpan Pengaturan Sosial Media
            </button>
          </div>
        )}

        {/* Terms Tab */}
        {activeTab === "terms" && (
          <div className="space-y-6">
            <div className="p-6 bg-white/5 border border-[#D4AF7A]/20 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#F7F5F2]">
                  Syarat & Ketentuan
                </h3>
                <button
                  onClick={() => {
                    const newId = Math.max(...terms.map((t) => t.id), 0) + 1;
                    setTerms([
                      ...terms,
                      {
                        id: newId,
                        title: "",
                        description: "",
                        details: [""],
                      },
                    ]);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20 text-sm font-medium"
                >
                  <Plus size={16} />
                  Tambah Syarat
                </button>
              </div>

              <div className="space-y-4">
                {terms.map((term, termIndex) => (
                  <div
                    key={term.id}
                    className="p-4 bg-white/5 border border-[#D4AF7A]/20 rounded-lg space-y-4"
                  >
                    {/* Header with delete button */}
                    <div className="flex items-start justify-between">
                      <span className="text-sm font-medium text-[#D4AF7A]">
                        Syarat #{termIndex + 1}
                      </span>
                      {terms.length > 1 && (
                        <button
                          onClick={() => {
                            if (confirm("Yakin ingin menghapus syarat ini?")) {
                              setTerms(terms.filter((t) => t.id !== term.id));
                            }
                          }}
                          className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex items-center justify-center"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                        Judul *
                      </label>
                      <input
                        type="text"
                        value={term.title}
                        onChange={(e) => {
                          const newTerms = [...terms];
                          newTerms[termIndex].title = e.target.value;
                          setTerms(newTerms);
                        }}
                        placeholder="Contoh: Booking Tanggal"
                        className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                        required
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                        Deskripsi *
                      </label>
                      <textarea
                        value={term.description}
                        onChange={(e) => {
                          const newTerms = [...terms];
                          newTerms[termIndex].description = e.target.value;
                          setTerms(newTerms);
                        }}
                        placeholder="Deskripsi singkat tentang syarat ini..."
                        rows={3}
                        className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                        required
                      />
                    </div>

                    {/* Details */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-[#F7F5F2]">
                          Detail Poin
                        </label>
                        <button
                          onClick={() => {
                            const newTerms = [...terms];
                            newTerms[termIndex].details = [
                              ...(newTerms[termIndex].details || []),
                              "",
                            ];
                            setTerms(newTerms);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded text-[#D4AF7A] hover:bg-[#D4AF7A]/20 text-xs font-medium"
                        >
                          <Plus size={14} />
                          Tambah Poin
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(term.details || []).map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={detail}
                              onChange={(e) => {
                                const newTerms = [...terms];
                                newTerms[termIndex].details[detailIndex] =
                                  e.target.value;
                                setTerms(newTerms);
                              }}
                              placeholder={`Detail poin ${detailIndex + 1}`}
                              className="flex-1 px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] text-sm"
                            />
                            {(term.details || []).length > 0 && (
                              <button
                                onClick={() => {
                                  const newTerms = [...terms];
                                  newTerms[termIndex].details = newTerms[
                                    termIndex
                                  ].details.filter((_, i) => i !== detailIndex);
                                  setTerms(newTerms);
                                }}
                                className="p-2 text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-[#F7F5F2]/60 mt-1">
                        Tambahkan detail poin untuk menjelaskan lebih lanjut
                        (opsional)
                      </p>
                    </div>
                  </div>
                ))}

                {terms.length === 0 && (
                  <div className="text-center py-12 bg-white/5 border border-dashed border-[#D4AF7A]/30 rounded-lg">
                    <FileText className="w-12 h-12 text-[#D4AF7A]/50 mx-auto mb-3" />
                    <p className="text-[#F7F5F2]/70">
                      Belum ada syarat & ketentuan
                    </p>
                    <button
                      onClick={() => {
                        setTerms([
                          {
                            id: 1,
                            title: "",
                            description: "",
                            details: [""],
                          },
                        ]);
                      }}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20 text-sm font-medium"
                    >
                      <Plus size={16} />
                      Tambah Syarat Pertama
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => handleSave("terms")}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              Simpan Syarat & Ketentuan
            </button>
          </div>
        )}

        {/* Sections Tab */}
        {activeTab === "sections" && (
          <div className="space-y-6">
            <div className="p-6 bg-white/5 border border-[#D4AF7A]/20 rounded-lg">
              <h3 className="text-lg font-semibold text-[#F7F5F2] mb-2">
                Tampilkan Bagian Halaman
              </h3>
              <p className="text-[#F7F5F2]/70 text-sm mb-6">
                Atur bagian mana saja yang ingin ditampilkan di halaman depan
              </p>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[
                  {
                    id: "hero",
                    name: "Hero Section",
                    icon: "🏠",
                    desc: "Bagian header utama dengan CTA",
                  },
                  {
                    id: "about",
                    name: "Tentang Kami",
                    icon: "👤",
                    desc: "Informasi tentang henna artist",
                  },
                  {
                    id: "services",
                    name: "Layanan",
                    icon: "📦",
                    desc: "Daftar paket dan harga",
                  },
                  {
                    id: "gallery",
                    name: "Galeri",
                    icon: "🖼️",
                    desc: "Portofolio hasil karya",
                  },
                  {
                    id: "certificates",
                    name: "Sertifikat",
                    icon: "📜",
                    desc: "Sertifikat dan penghargaan",
                  },
                  {
                    id: "testimonials",
                    name: "Testimoni",
                    icon: "⭐",
                    desc: "Ulasan dari klien",
                  },
                  {
                    id: "contact",
                    name: "Kontak",
                    icon: "📞",
                    desc: "Form dan info kontak",
                  },
                ].map((section) => (
                  <div
                    key={section.id}
                    className={`p-4 border rounded-lg transition-all ${
                      sectionSettings[section.id]
                        ? "bg-[#D4AF7A]/10 border-[#D4AF7A]/30"
                        : "bg-white/5 border-[#D4AF7A]/20"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{section.icon}</span>
                        <div>
                          <h4 className="font-semibold text-[#F7F5F2]">
                            {section.name}
                          </h4>
                          <p className="text-xs text-[#F7F5F2]/60">
                            {section.desc}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setSectionSettings({
                            ...sectionSettings,
                            [section.id]: !sectionSettings[section.id],
                          })
                        }
                        className={`p-2 flex items-center justify-center rounded-lg transition-all ${
                          sectionSettings[section.id]
                            ? "bg-[#D4AF7A] text-black"
                            : "bg-white/10 text-[#F7F5F2]/70 hover:text-[#F7F5F2]"
                        }`}
                      >
                        {sectionSettings[section.id] ? (
                          <Eye size={18} />
                        ) : (
                          <EyeOff size={18} />
                        )}
                      </button>
                    </div>
                    <p className="mt-2 text-xs">
                      Status:{" "}
                      {sectionSettings[section.id] ? (
                        <span className="text-[#D4AF7A] font-medium">
                          Ditampilkan
                        </span>
                      ) : (
                        <span className="text-[#F7F5F2]/50">Disembunyikan</span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => handleSave("sections")}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              Simpan Pengaturan Bagian
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
